/**
 * Command Center motion primitives (GSAP) — local feasibility layer.
 *
 * Uses ``window.gsap`` when already present (same-origin / allowed by CSP).
 * Does NOT inject a third-party CDN script — dashboard CSP is ``script-src 'self'``.
 * When GSAP is absent, callers no-op and the static UI remains usable.
 *
 * @see https://github.com/greensock/gsap-skills (gsap-core · gsap-performance)
 */

/** @type {Promise<typeof gsap> | null} */
let gsapLoadPromise = null;

function loadGsapScript() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.gsap) return Promise.resolve(window.gsap);
  if (gsapLoadPromise) return gsapLoadPromise;
  // Optional enhancement only — never widen CSP for jsDelivr/CDN.
  gsapLoadPromise = Promise.reject(new Error("gsap unavailable under CSP script-src self"));
  void gsapLoadPromise.catch(() => {});
  return gsapLoadPromise;
}

/** @type {import("gsap").gsap.core.Tween | null} */
let activePageTween = null;

/**
 * Subtle page content entrance for CC shell routes.
 * @param {ParentNode | null} root
 * @param {{ selector?: string }} [opts]
 * @returns {() => void} revert / kill
 */
export function animateCcPageEnter(root, opts = {}) {
  if (!root) return () => {};
  const sel = opts.selector || ".croc-cc-page-content > *:not(.route-loading)";
  const nodes = root.querySelectorAll(sel);
  if (!nodes.length) return () => {};

  let mm = null;
  let killed = false;

  const run = async () => {
    try {
      const gsap = await loadGsapScript();
      if (killed || !root.isConnected) return;
      if (activePageTween) {
        try {
          activePageTween.kill();
        } catch (_) {}
        activePageTween = null;
      }
      if (typeof gsap.matchMedia !== "function") {
        activePageTween = gsap.from(nodes, {
          autoAlpha: 0,
          y: 10,
          duration: 0.35,
          ease: "power2.out",
          stagger: 0.04,
          clearProps: "opacity,visibility,transform",
        });
        return;
      }
      mm = gsap.matchMedia();
      mm.add(
        {
          reduceMotion: "(prefers-reduced-motion: reduce)",
        },
        (ctx) => {
          const { reduceMotion } = ctx.conditions;
          if (reduceMotion) {
            gsap.set(nodes, { autoAlpha: 1, y: 0 });
            return;
          }
          activePageTween = gsap.from(nodes, {
            autoAlpha: 0,
            y: 10,
            duration: 0.35,
            ease: "power2.out",
            stagger: 0.04,
            clearProps: "opacity,visibility,transform",
          });
        },
      );
    } catch (_) {
      /* GSAP optional — static UI remains usable */
    }
  };

  void run();

  return () => {
    killed = true;
    try {
      if (activePageTween) activePageTween.kill();
    } catch (_) {}
    activePageTween = null;
    try {
      if (mm && typeof mm.revert === "function") mm.revert();
    } catch (_) {}
  };
}

/**
 * Stagger feed / list rows (overview incidents, audit timeline).
 * @param {NodeListOf<Element> | Element[]} nodes
 */
export async function animateCcFeedStagger(nodes) {
  const list = Array.from(nodes || []);
  if (!list.length) return;
  try {
    const gsap = await loadGsapScript();
    if (typeof gsap.matchMedia === "function") {
      const mm = gsap.matchMedia();
      mm.add({ reduceMotion: "(prefers-reduced-motion: reduce)" }, (ctx) => {
        if (ctx.conditions.reduceMotion) {
          gsap.set(list, { autoAlpha: 1, y: 0 });
          return;
        }
        gsap.from(list, {
          autoAlpha: 0,
          y: 8,
          duration: 0.28,
          ease: "power2.out",
          stagger: 0.03,
          clearProps: "opacity,visibility,transform",
        });
      });
      return;
    }
    gsap.from(list, {
      autoAlpha: 0,
      y: 8,
      duration: 0.28,
      ease: "power2.out",
      stagger: 0.03,
      clearProps: "opacity,visibility,transform",
    });
  } catch (_) {}
}
